import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { CheckSquare, Plus, Circle, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const PRIORITIES = [
  { value: 'high',   label: 'High',   color: 'text-red-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-500' },
  { value: 'low',    label: 'Low',    color: 'text-blue-400' },
];

const RemindersWidget = () => {
  const { user, refreshTick } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', priority: 'medium', due_date: '' });
  const [saving, setSaving] = useState(false);

  const fetchReminders = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('reminders')
        .select('*')
        .eq('user_id', user.id)
        .order('completed', { ascending: true })
        .order('due_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      setReminders(data ?? []);
    } catch (err) {
      console.error('RemindersWidget fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchReminders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshTick]);

  const toggleReminder = async (reminder) => {
    setReminders((prev) =>
      prev.map((r) => (r.id === reminder.id ? { ...r, completed: !r.completed } : r))
    );
    try {
      const { error } = await supabase
        .from('reminders')
        .update({ completed: !reminder.completed })
        .eq('id', reminder.id);
      if (error) throw error;
      await fetchReminders();
    } catch (err) {
      console.error('RemindersWidget toggle error:', err);
      setReminders((prev) =>
        prev.map((r) => (r.id === reminder.id ? { ...r, completed: reminder.completed } : r))
      );
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('reminders').insert({
        user_id: user.id,
        title: form.title.trim(),
        priority: form.priority,
        due_date: form.due_date || null,
      });
      if (error) throw error;
      setShowModal(false);
      setForm({ title: '', priority: 'medium', due_date: '' });
      await fetchReminders();
    } catch (err) {
      console.error('RemindersWidget create error:', err);
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (priority) =>
    PRIORITIES.find((p) => p.value === priority)?.color ?? 'text-gray-500';

  return (
    <>
      <Card className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none hover:shadow-[0_8px_30px_rgba(99,102,241,0.1)] dark:hover:shadow-none transition-all duration-300 overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-indigo-50/50 dark:border-[#1a1a20]">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[rgba(99,102,241,0.08)] dark:bg-[rgba(167,139,250,0.1)] rounded-xl">
                <CheckSquare className="text-indigo-500 dark:text-violet-400" size={24} />
              </div>
              <h2 className="text-xl font-semibold text-[#1a1625] dark:text-[#e5e5e5]">Reminders</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400"
              onClick={() => setShowModal(true)}
            >
              <Plus size={16} />
            </Button>
          </div>

          {/* Reminders List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 rounded-xl bg-indigo-50/50 dark:bg-[#16161b] animate-pulse" />
                ))}
              </div>
            ) : reminders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <CheckSquare size={28} className="text-indigo-200 dark:text-[#333] mb-2" />
                <p className="text-sm text-[#6b7280] dark:text-[#888]">No reminders yet</p>
                <p className="text-xs text-[#9ca3af] dark:text-[#555]">Tap + to add one</p>
              </div>
            ) : (
              reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className="flex items-center space-x-3 p-3 bg-gradient-to-br from-indigo-50/30 to-white dark:from-[#16161b] dark:to-[#111114] rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer border border-indigo-50/80 dark:border-[#1a1a20]"
                  onClick={() => toggleReminder(reminder)}
                >
                  {reminder.completed ? (
                    <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle
                      size={20}
                      className={`${getPriorityColor(reminder.priority)} flex-shrink-0`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium ${
                        reminder.completed
                          ? 'line-through text-[#9ca3af] dark:text-[#555]'
                          : 'text-[#1a1625] dark:text-[#e5e5e5]'
                      }`}
                    >
                      {reminder.title}
                    </p>
                    {reminder.due_date && (
                      <p className="text-xs text-[#6b7280] dark:text-[#888]">
                        {new Date(reminder.due_date + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>

      {/* Create Reminder Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#111114] rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 border border-white/90 dark:border-[#1e1e24]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1a1625] dark:text-[#e5e5e5]">New Reminder</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#9ca3af] hover:text-[#6b7280] dark:text-[#555] dark:hover:text-[#888] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Reminder title"
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500 placeholder-[#9ca3af] dark:placeholder-[#555]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-2 block">Priority</label>
                <div className="flex space-x-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setForm({ ...form, priority: p.value })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        form.priority === p.value
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-transparent'
                          : 'bg-white dark:bg-[#1a1a20] text-[#6b7280] dark:text-[#888] border-indigo-100 dark:border-[#1e1e24] hover:border-indigo-300 dark:hover:border-violet-800'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500"
                />
              </div>
            </div>

            <div className="flex space-x-2 mt-5">
              <Button
                variant="outline"
                className="flex-1 border-indigo-100 dark:border-[#1e1e24]"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                onClick={handleCreate}
                disabled={saving || !form.title.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RemindersWidget;
